import { type FC, useMemo } from "react";

import { RichTooltip } from "~/components/UI/RichTooltip";
import { logoByPlatform, markByPlatform } from "~/constants/platfroms";
import type { Platform } from "~/types/platform";
import { splitPlatformLogos } from "~/utils/splitPlatformLogos";

import * as Styled from "./PlatformIcons.styles";

interface IPlatformIconsProps {
  platforms: Platform[] | Platform;
  size?: number;
  variant?: "stack" | "marks";
  max?: number;
}

const toList = (platforms: Platform[] | Platform): Platform[] =>
  Array.isArray(platforms) ? platforms : [platforms];

const PlatformIconsStack: FC<{ platforms: Platform[]; size: number }> = ({
  platforms,
  size,
}) => {
  const icons = useMemo(() => {
    const uniquePlatforms = Array.from(new Set(platforms));

    return uniquePlatforms
      .map((platform) => ({
        platform,
        src: logoByPlatform[platform],
      }))
      .filter((item): item is { platform: Platform; src: string } =>
        Boolean(item.src)
      );
  }, [platforms]);

  if (icons.length === 0) return null;

  return (
    <Styled.PlatformIconsWrapper>
      {icons.map(({ platform, src }, index) => (
        <Styled.PlatformIcon
          key={platform}
          src={src}
          alt={platform}
          size={size}
          isFirst={index === 0}
          style={{ zIndex: icons.length - index }}
        />
      ))}
    </Styled.PlatformIconsWrapper>
  );
};

const PlatformMark: FC<{ platform: Platform; size: number }> = ({
  platform,
  size,
}) => <Styled.LogoMark src={markByPlatform[platform]} alt={platform} size={size} />;

const PlatformMarks: FC<{
  platforms: Platform[];
  size: number;
  max?: number;
}> = ({ platforms, size, max }) => {
  const { visible, hiddenNames, overflowLabel } = useMemo(
    () => splitPlatformLogos(platforms, max),
    [platforms, max]
  );

  if (visible.length === 0) return null;

  return (
    <Styled.MarksWrapper>
      {visible.map((platform) => (
        <PlatformMark key={platform} platform={platform} size={size} />
      ))}
      {overflowLabel && (
        <RichTooltip title={hiddenNames.join(", ")}>
          <Styled.MarksOverflow>{overflowLabel}</Styled.MarksOverflow>
        </RichTooltip>
      )}
    </Styled.MarksWrapper>
  );
};

export const PlatformIcons: FC<IPlatformIconsProps> = ({
  platforms,
  size = 20,
  variant = "stack",
  max,
}) => {
  const list = useMemo(() => toList(platforms), [platforms]);

  if (variant === "marks") {
    return <PlatformMarks platforms={list} size={size} max={max} />;
  }

  return <PlatformIconsStack platforms={list} size={size} />;
};
